import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { User } from '../types';
import { 
  Shield, 
  Lock, 
  Unlock, 
  RotateCcw, 
  RefreshCw, 
  X, 
  Search, 
  CheckCircle2, 
  AlertCircle,
  Copy,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import './MFAManagement.css';

const MFAManagement: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['users', search],
    async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    }
  );

  const users: User[] = data?.users || [];

  // Calculate MFA statistics
  const mfaStats = {
    total: users.length,
    enabled: users.filter(u => u.mfaEnabled).length,
    disabled: users.filter(u => !u.mfaEnabled).length,
    needsSetup: 0, // Will be calculated from status
  };

  const handleSelectUser = async (user: User) => {
    setSelectedUser(user);
    setShowBackupCodes(false);
    setBackupCodes([]);
    setShowSessions(false);
    try {
      const response = await api.get(`/users/${user.id}/mfa`);
      setMfaStatus(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load MFA status');
    }
  };

  const enableMFAMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/reset`, { reason });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('MFA enabled successfully. User will need to set up MFA on next login.');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        queryClient.invalidateQueries('users');
        if (selectedUser) {
          handleSelectUser(selectedUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to enable MFA');
      },
    }
  );

  const disableMFAMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/disable`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('MFA disabled successfully');
        queryClient.invalidateQueries('users');
        if (selectedUser) {
          handleSelectUser(selectedUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to disable MFA');
      },
    }
  );

  const resetMFAMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/reset`, { reason });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('MFA reset successfully. User will need to set up MFA on next login.');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        queryClient.invalidateQueries('users');
        if (selectedUser) {
          handleSelectUser(selectedUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to reset MFA');
      },
    }
  );

  const regenerateBackupCodesMutation = useMutation(
    async ({ id, reason }: { id: string; reason?: string }) => {
      const response = await api.post(`/users/${id}/mfa/regenerate-backup-codes`, { reason });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Backup codes regenerated successfully');
        setBackupCodes(data.backupCodes || []);
        setShowBackupCodes(true);
        if (selectedUser) {
          handleSelectUser(selectedUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to regenerate backup codes');
      },
    }
  );

  const revokeSessionMutation = useMutation(
    async ({ userId, sessionId }: { userId: string; sessionId: string }) => {
      const response = await api.delete(`/users/${userId}/sessions/${sessionId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Session revoked successfully');
        if (selectedUser) {
          handleSelectUser(selectedUser);
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to revoke session');
      },
    }
  );

  const handleEnableMFA = (user: User) => {
    const reason = prompt('Reason for enabling MFA (optional):');
    if (window.confirm('This will enable MFA for this user. They will need to set up MFA on next login. Continue?')) {
      enableMFAMutation.mutate({ id: user.id, reason: reason || undefined });
    }
  };

  const handleDisableMFA = (user: User) => {
    const reason = prompt('Reason for disabling MFA (optional):');
    if (window.confirm('This will disable MFA for this user and revoke all sessions. Continue?')) {
      disableMFAMutation.mutate({ id: user.id, reason: reason || undefined });
    }
  };

  const handleResetMFA = (user: User) => {
    const reason = prompt('Reason for resetting MFA (optional):');
    if (window.confirm('This will reset MFA and generate new secret and backup codes. User will need to set up MFA again on next login. Continue?')) {
      resetMFAMutation.mutate({ id: user.id, reason: reason || undefined });
    }
  };

  const handleRegenerateBackupCodes = (user: User) => {
    const reason = prompt('Reason for regenerating backup codes (optional):');
    if (window.confirm('This will generate new backup codes. Old codes will no longer work. Continue?')) {
      regenerateBackupCodesMutation.mutate({ id: user.id, reason: reason || undefined });
    }
  };

  const handleRevokeSession = (sessionId: string) => {
    if (selectedUser && window.confirm('Are you sure you want to revoke this session? The user will be logged out from this device.')) {
      revokeSessionMutation.mutate({ userId: selectedUser.id, sessionId });
    }
  };

  return (
    <div className="mfa-management-page">
      <div className="page-header">
        <div>
          <h1>MFA Management</h1>
          <p>Manage Multi-Factor Authentication for all users</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}>
            <Shield size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{mfaStats.total}</div>
            <div className="stat-label">Total Users</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b981' }}>
            <CheckCircle2 size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{mfaStats.enabled}</div>
            <div className="stat-label">MFA Enabled</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#ef4444' }}>
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{mfaStats.disabled}</div>
            <div className="stat-label">MFA Disabled</div>
          </div>
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="mfa-management-layout">
        <div className="users-list-panel">
          <h3>Users</h3>
          {isLoading ? (
            <div className="loading">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="empty-state">No users found</div>
          ) : (
            <div className="users-list">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-item-header">
                    <div className="user-avatar">
                      <Shield size={20} />
                    </div>
                    <div className="user-info">
                      <div className="user-name">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.username}
                      </div>
                      <div className="user-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="user-mfa-status">
                    {user.mfaEnabled ? (
                      <span className="mfa-badge enabled">
                        <CheckCircle2 size={14} />
                        <span>Enabled</span>
                      </span>
                    ) : (
                      <span className="mfa-badge disabled">
                        <AlertCircle size={14} />
                        <span>Disabled</span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mfa-details-panel">
          {selectedUser && mfaStatus ? (
            <>
              <div className="panel-header">
                <h2>MFA Details - {mfaStatus.name || mfaStatus.email}</h2>
                <button
                  className="close-btn"
                  onClick={() => {
                    setSelectedUser(null);
                    setMfaStatus(null);
                    setShowBackupCodes(false);
                    setBackupCodes([]);
                    setShowSessions(false);
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mfa-details-content">
                <div className="mfa-status-section">
                  <h3>Current Status</h3>
                  <div className="mfa-status-info">
                    <div className="info-row">
                      <span className="info-label">MFA Status:</span>
                      <span className={`mfa-badge ${mfaStatus.mfaEnabled ? 'enabled' : 'disabled'}`}>
                        {mfaStatus.mfaEnabled ? (
                          <>
                            <CheckCircle2 size={16} />
                            <span>Enabled</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle size={16} />
                            <span>Disabled</span>
                          </>
                        )}
                      </span>
                    </div>
                    {mfaStatus.mfaEnabled && (
                      <div className="info-row">
                        <span className="info-label">Backup Codes Available:</span>
                        <span>{mfaStatus.backupCodesCount} codes</span>
                      </div>
                    )}
                    {mfaStatus.activeSessions && mfaStatus.activeSessions.length > 0 && (
                      <div className="info-row">
                        <span className="info-label">Active Devices:</span>
                        <span>{mfaStatus.activeSessions.length} device(s)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Active Sessions/Devices Section */}
                {mfaStatus.activeSessions && mfaStatus.activeSessions.length > 0 && (
                  <div className="sessions-section">
                    <div className="sessions-header">
                      <h3>Active Devices & Sessions</h3>
                      <button
                        className="toggle-sessions-btn"
                        onClick={() => setShowSessions(!showSessions)}
                      >
                        {showSessions ? <EyeOff size={16} /> : <Eye size={16} />}
                        <span>{showSessions ? 'Hide' : 'Show'} Sessions</span>
                      </button>
                    </div>
                    {showSessions && (
                      <div className="sessions-list">
                        {mfaStatus.activeSessions.map((session: any) => (
                          <div key={session.id} className="session-item">
                            <div className="session-info">
                              <div className="session-device-header">
                                <div className="session-device">
                                  <Shield size={16} />
                                  <div>
                                    <div className="device-name">{session.deviceName || session.device || 'Unknown Device'}</div>
                                    <div className="device-browser-os">
                                      {session.browser && <span className="device-browser">{session.browser}</span>}
                                      {session.os && <span className="device-os">{session.os}</span>}
                                      {session.deviceType && (
                                        <span className={`device-type ${session.deviceType.toLowerCase()}`}>
                                          {session.deviceType}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="session-ip-address">
                                  {session.publicIP ? (
                                    <>
                                      <div className="ip-address-row">
                                        <strong>Public IP:</strong> 
                                        <span className="ip-value public-ip">{session.publicIP}</span>
                                      </div>
                                      {session.localIP && (
                                        <div className="ip-address-row">
                                          <strong>Local IP:</strong> 
                                          <span className="ip-value local-ip">{session.localIP}</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="ip-address-row">
                                      <strong>IP Address:</strong> 
                                      <span className="ip-value">{session.ipAddress || 'Unknown IP'}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="session-details">
                                <span className="session-time">
                                  <strong>Connected:</strong> {new Date(session.createdAt).toLocaleString()}
                                </span>
                                {session.expiresAt && (
                                  <span className="session-expires">
                                    <strong>Expires:</strong> {new Date(session.expiresAt).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              className="btn-danger-small"
                              onClick={() => handleRevokeSession(session.id)}
                              disabled={revokeSessionMutation.isLoading}
                              title="Revoke this session"
                            >
                              <Trash2 size={14} />
                              <span>Revoke</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Backup Codes Display */}
                {showBackupCodes && backupCodes.length > 0 && (
                  <div className="backup-codes-section">
                    <h3>Backup Codes</h3>
                    <p className="backup-codes-warning">
                      ⚠️ Save these codes securely. They will not be shown again.
                    </p>
                    <div className="backup-codes-grid">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="backup-code-item">
                          <code>{code}</code>
                          <button
                            className="copy-code-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(code);
                              toast.success('Code copied to clipboard');
                            }}
                            title="Copy code"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        const allCodes = backupCodes.join('\n');
                        navigator.clipboard.writeText(allCodes);
                        toast.success('All codes copied to clipboard');
                      }}
                    >
                      <Copy size={16} />
                      <span>Copy All Codes</span>
                    </button>
                  </div>
                )}

                {/* Actions Section */}
                <div className="mfa-actions-section">
                  <h3>Actions</h3>
                  <div className="mfa-actions-grid">
                    {mfaStatus.mfaEnabled ? (
                      <>
                        <button
                          className="btn-action danger"
                          onClick={() => handleDisableMFA(selectedUser)}
                          disabled={disableMFAMutation.isLoading}
                        >
                          <Unlock size={16} />
                          <span>{disableMFAMutation.isLoading ? 'Disabling...' : 'Disable MFA'}</span>
                        </button>
                        <button
                          className="btn-action"
                          onClick={() => handleResetMFA(selectedUser)}
                          disabled={resetMFAMutation.isLoading}
                        >
                          <RotateCcw size={16} />
                          <span>{resetMFAMutation.isLoading ? 'Resetting...' : 'Reset MFA'}</span>
                        </button>
                        <button
                          className="btn-action"
                          onClick={() => handleRegenerateBackupCodes(selectedUser)}
                          disabled={regenerateBackupCodesMutation.isLoading}
                        >
                          <RefreshCw size={16} />
                          <span>{regenerateBackupCodesMutation.isLoading ? 'Regenerating...' : 'Regenerate Backup Codes'}</span>
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn-action primary"
                        onClick={() => handleEnableMFA(selectedUser)}
                        disabled={enableMFAMutation.isLoading}
                      >
                        <Lock size={16} />
                        <span>{enableMFAMutation.isLoading ? 'Enabling...' : 'Enable MFA'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <Shield size={48} />
              <h3>Select a user to manage MFA</h3>
              <p>Click on a user from the list to view and manage their MFA settings</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFAManagement;

